import InputField from '../../components/auth/InputField';
import SelectField from '../../components/auth/SelectField';
import { regions } from '../../data/regions';
import { locationsByRegion } from '../../data/locationsByRegion';

export default function PersonalContactStep({ formData, onChange, showVehicleFields = false }) {
  const ageOptions = ['18-23', '24-35', '36-45', '46-55', '55+'];
  const genderOptions = ['male', 'female'];
  const regionNames = regions.map(region => region.name);

  // Get locations based on selected region
  const getLocationOptions = () => {
    if (!formData.region) return [];
    
    // Find the region ID from the region name
    const selectedRegion = regions.find(region => region.name === formData.region);
    if (!selectedRegion) return [];
    
    return locationsByRegion[selectedRegion.id] || [];
  };

  const locationOptions = getLocationOptions();
  const isCustomLocation = formData.location === 'Other';

  // Validate Ghana phone number format
  const isValidGhanaPhone = (value) => {
    if (!value) return false;
    
    const digits = value.replace(/\D/g, '');
    
    // Ghana phone numbers: 10 digits starting with 0, or 12 digits starting with 233
    if (digits.startsWith('0') && digits.length === 10) {
      return true;
    }
    if (digits.startsWith('233') && digits.length === 12) {
      return true;
    }
    
    return false;
  };

  // Format phone number for API (behind the scenes)
  const formatPhoneForAPI = (value) => {
    if (!value) return '';
    
    // Remove all non-digits
    const digits = value.replace(/\D/g, '');
    
    // If it starts with 233, keep as is
    if (digits.startsWith('233')) {
      return digits;
    }
    
    // If it starts with 0, replace with 233
    if (digits.startsWith('0')) {
      return '233' + digits.substring(1);
    }
    
    // If it doesn't start with 0 or 233, assume it's a local number and add 233
    if (digits.length > 0 && !digits.startsWith('233') && !digits.startsWith('0')) {
      return '233' + digits;
    }
    
    return digits;
  };

  // Handle phone number input - preserve user input for display
  const handlePhoneChange = (e) => {
    const userInput = e.target.value;
    const digits = userInput.replace(/\D/g, '');
    
    // Limit input length based on format
    let maxLength = 15; // Default max length
    if (digits.startsWith('0')) {
      maxLength = 10; // 0XXXXXXXXX
    } else if (digits.startsWith('233')) {
      maxLength = 12; // 233XXXXXXXXXX
    }
    
    // Truncate if exceeds max length
    if (digits.length > maxLength) {
      const truncated = userInput.slice(0, userInput.length - (digits.length - maxLength));
      const apiFormatted = formatPhoneForAPI(truncated);
      onChange('phone_number', truncated);
      onChange('phone_number_api', apiFormatted);
      return;
    }
    
    const apiFormatted = formatPhoneForAPI(userInput);
    
    // Store both the user input (for display) and API formatted version
    onChange('phone_number', userInput);
    onChange('phone_number_api', apiFormatted);
  };

  // Handle license plate prefix input (GR)
  const handleLicensePrefixChange = (e) => {
    const value = e.target.value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 2);
    onChange('license_prefix', value);
  };

  // Handle license plate middle input (1-4 digits)
  const handleLicenseMiddleChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 4);
    onChange('license_middle', value);
  };

  // Handle license plate suffix input (2 digits)
  const handleLicenseSuffixChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 2);
    onChange('license_suffix', value);
  };

  // Combine license plate parts
  const getFullLicensePlate = () => {
    const prefix = formData.license_prefix || '';
    const middle = formData.license_middle || '';
    const suffix = formData.license_suffix || '';
    
    if (!prefix && !middle && !suffix) return '';
    
    let fullPlate = prefix;
    if (middle) fullPlate += '-' + middle;
    if (suffix) fullPlate += '-' + suffix;
    
    return fullPlate;
  };

  // Validate license plate format
  const isValidLicensePlate = () => {
    const prefix = formData.license_prefix || '';
    const middle = formData.license_middle || '';
    const suffix = formData.license_suffix || '';
    
    // Must have prefix (2 letters), middle (1-4 digits), and suffix (2 digits)
    return prefix.length === 2 && 
           middle.length >= 1 && middle.length <= 4 && 
           suffix.length === 2;
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <InputField type="text" placeholder="First Name" value={formData.first_name} onChange={(e) => onChange('first_name', e.target.value)} delay={0.2} />
        <InputField type="text" placeholder="Last Name" value={formData.last_name} onChange={(e) => onChange('last_name', e.target.value)} delay={0.25} />
      </div>
      <InputField type="email" placeholder="Email" value={formData.email} onChange={(e) => onChange('email', e.target.value)} delay={0.3} />
      <div className="space-y-1">
        <InputField 
          type="tel" 
          placeholder="Phone Number (e.g., 0249158582)" 
          value={formData.phone_number} 
          onChange={handlePhoneChange} 
          delay={0.35} 
        />
        {formData.phone_number && !isValidGhanaPhone(formData.phone_number) && (
          <p className="text-xs text-red-500">
            Please enter a valid Ghana phone number (10 digits starting with 0, or 12 digits starting with 233)
          </p>
        )}
        {formData.phone_number && isValidGhanaPhone(formData.phone_number) && (
          <p className="text-xs text-green-600">
            ✓ Valid phone number format
          </p>
        )}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <SelectField placeholder="Gender" value={formData.gender} onChange={(v) => onChange('gender', v)} options={genderOptions} delay={0.4} />
        <SelectField placeholder="Age Range" value={formData.age} onChange={(v) => onChange('age', v)} options={ageOptions} delay={0.45} />
      </div>
      <div className="space-y-3">
        <SelectField 
          placeholder="Region" 
          value={formData.region} 
          onChange={(v) => {
            onChange('region', v);
            // Reset location when region changes
            onChange('location', '');
            onChange('custom_location', '');
          }} 
          options={regionNames} 
          delay={0.5}
          searchable={true}
        />
        
        {formData.region && (
          <div className="space-y-2">
            <SelectField 
              placeholder="Location" 
              value={formData.location} 
              onChange={(v) => {
                onChange('location', v);
                // Clear custom location if not "Other"
                if (v !== 'Other') {
                  onChange('custom_location', '');
                }
              }} 
              options={locationOptions} 
              delay={0.55}
              searchable={true}
            />
            
            {isCustomLocation && (
              <InputField 
                type="text" 
                placeholder="Enter your location" 
                value={formData.custom_location || ''} 
                onChange={(e) => onChange('custom_location', e.target.value)} 
                delay={0.6} 
              />
            )}
          </div>
        )}
      </div>
      {showVehicleFields && (
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">License Plate Number</label>
          <div className="flex items-center gap-2">
            {/* Prefix Input (2 letters) */}
            <input
              type="text"
              placeholder="GR"
              value={formData.license_prefix || ''}
              onChange={handleLicensePrefixChange}
              maxLength={2}
              className="w-16 px-3 py-3 text-sm text-center rounded-xl border border-gray-200 outline-none bg-white/95 focus:border-emerald-600 focus:ring-2 focus:ring-emerald-100 uppercase"
            />
            <span className="text-gray-400">-</span>
            
            {/* Middle Input (1-4 digits) */}
            <input
              type="text"
              placeholder="1234"
              value={formData.license_middle || ''}
              onChange={handleLicenseMiddleChange}
              maxLength={4}
              className="w-20 px-3 py-3 text-sm text-center rounded-xl border border-gray-200 outline-none bg-white/95 focus:border-emerald-600 focus:ring-2 focus:ring-emerald-100"
            />
            <span className="text-gray-400">-</span>
            
            {/* Suffix Input (2 digits) */}
            <input
              type="text"
              placeholder="21"
              value={formData.license_suffix || ''}
              onChange={handleLicenseSuffixChange}
              maxLength={2}
              className="w-16 px-3 py-3 text-sm text-center rounded-xl border border-gray-200 outline-none bg-white/95 focus:border-emerald-600 focus:ring-2 focus:ring-emerald-100"
            />
          </div>
          
          {/* Validation Feedback */}
          {(formData.license_prefix || formData.license_middle || formData.license_suffix) && (
            <div className="space-y-1">
              {!isValidLicensePlate() && (
                <p className="text-xs text-red-500">
                  Please complete all fields: 2 letters, 1-4 digits, 2 digits
                </p>
              )}
              {isValidLicensePlate() && (
                <p className="text-xs text-green-600">
                  ✓ Valid license plate: {getFullLicensePlate()}
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


